/**
 * Lead-card generator — produces schema-accurate `LeadCardResponse[]` matching
 * the `leads_cards` N8N action response (types/n8nResponses.ts:111).
 *
 * The leads_by_status / total_leads counters on each parent campaign are
 * recomputed from the generated leads so campaign summary cards stay
 * consistent with the cards rendered in the Kanban.
 */

import type {
  CampaignMetrics,
  LeadCardResponse,
} from '../../../types/n8nResponses';
import type { MockVolume } from '../mockDataVolume';
import { mulberry32, intBetween, pick, chance, weightedPick } from '../rng';
import {
  FIRST_NAMES, LAST_NAMES, ROLES, SENIORITY,
  COMPANY_PREFIXES, COMPANY_SUFFIXES, INDUSTRIES,
  COUNTRIES, COUNTRY_CODES, CITIES, TIMEZONES,
  EMPLOYEE_RANGES, REVENUE_RANGES,
} from '../dictionaries';
import { targetLeadCountFor } from './campaignGenerator';

/**
 * The global lead-status enum from N8N (the raw values that appear in
 * LeadCardResponse.status). Mapping to the UniBox funnel keys happens in the
 * leadToUniBoxMapper — here we stay faithful to the N8N schema.
 */
export type N8nLeadStatus =
  | 'new_lead' | 'enriching' | 'enriched' | 'outreaching'
  | 'outreached' | 'engaged' | 'proposal' | 'converted' | 'discarded';

// Realistic funnel distribution. Top-heavy: most leads are new/enriching, a
// thin tail converts. This makes every Kanban column populated.
const STATUS_WEIGHTS: ReadonlyArray<readonly [N8nLeadStatus, number]> = [
  ['new_lead', 6],
  ['enriching', 5],
  ['enriched', 4],
  ['outreaching', 4],
  ['outreached', 2],
  ['engaged', 2],
  ['proposal', 1],
  ['converted', 1],
  ['discarded', 2],
];

/**
 * Per-status substatus vocabulary for the mock — mirrors the DB contract
 * (leads arrive with status + substatus; transitional "…" entries are
 * system-owned). A portion of leads deliberately carries NO substatus so the
 * no-substatus rendering path stays exercised.
 */
const SUBSTATUS_BY_STATUS: ReadonlyArray<readonly [N8nLeadStatus, string[]]> = [
  ['new_lead', ['ready']],
  ['enriched', ['ready', 'writting_message', 'message_ready', 'ready_to_outreach']],
  ['outreaching', ['not_started', 'icebreaker', 'follow_up', 'last_message']],
  ['outreached', ['awaiting']],
  ['engaged', ['reply_received', 'in_conversation', 'meeting_requested', 'meeting_scheduled', 'discovery_completed', 'qualified', 'objection']],
  ['proposal', ['proposal_prepared', 'review_scheduled', 'objections_resolved', 'decision_process_identified', 'next_action_scheduled']],
  ['converted', ['closed_won', 'onboarding', 'active', 'upsell', 'support', 'at_risk']],
  ['discarded', ['not_interested', 'wrong_person', 'out_of_icp', 'bad_timing', 'no_response', 'bounced']],
];

const substatusFor = (status: N8nLeadStatus, rng: () => number): string | null => {
  // ~20% of leads carry no substatus (legacy rows).
  if (chance(0.2, rng)) return null;
  const entry = SUBSTATUS_BY_STATUS.find(([key]) => key === status);
  return entry && entry[1].length > 0 ? pick(entry[1], rng) : null;
};

const fitForScore = (score: number): LeadCardResponse['icp_fit'] => {
  if (score >= 85) return 'ideal';
  if (score >= 70) return 'good';
  if (score >= 50) return 'moderate';
  return 'poor';
};

const toIsoDaysAgo = (days: number): string => {
  const base = new Date('2026-07-01T12:00:00Z').getTime();
  return new Date(base - days * 86_400_000).toISOString();
};

/**
 * Generate leads for a single campaign. Returns the leads and the recomputed
 * metrics counters, so the caller can stamp them back onto the campaign.
 */
export const generateMockLeadsForCampaign = (
  campaign: CampaignMetrics,
  volume: Exclude<MockVolume, 'none'>,
): { leads: LeadCardResponse[]; metrics: CampaignMetrics['leads_by_status']; total: number } => {
  // Deterministic per campaign — same campaign id → same lead set, regardless
  // of where in the list the campaign sits.
  const campaignSeed = mulberry32(
    campaign.campaign_id.split('').reduce((h, c) => (h * 31 + c.charCodeAt(0)) >>> 0, 7),
  );
  const baseLeadCount = targetLeadCountFor(volume);
  // Vary slightly per campaign so columns aren't identical sizes.
  const count = Math.max(0, Math.round(baseLeadCount * (0.7 + campaignSeed() * 0.6)));

  const leads: LeadCardResponse[] = [];
  const metrics: CampaignMetrics['leads_by_status'] = {
    new_lead: 0, enriching: 0, enriched: 0, outreaching: 0,
    outreached: 0, engaged: 0, proposal: 0, converted: 0, discarded: 0,
  };

  for (let i = 0; i < count; i++) {
    const rng = mulberry32(20000 + i * 37 + (count % 1000));
    const status = weightedPick(STATUS_WEIGHTS, rng);
    metrics[status] += 1;

    const firstName = pick(FIRST_NAMES, rng);
    const lastName = pick(LAST_NAMES, rng);
    const personName = `${firstName} ${lastName}`;
    const company = `${pick(COMPANY_PREFIXES, rng)} ${pick(COMPANY_SUFFIXES, rng)}`;
    const industry = pick(INDUSTRIES, rng);
    const countryIdx = intBetween(0, COUNTRIES.length - 1, rng);
    const country = COUNTRIES[countryIdx];
    const countryCode = COUNTRY_CODES[countryIdx % COUNTRY_CODES.length];
    const city = CITIES[countryIdx % CITIES.length];
    const timezone = TIMEZONES[countryIdx % TIMEZONES.length];

    // Channel enrichment: leads further down the funnel have more channels.
    const enrichmentDepth =
      status === 'new_lead' ? 0
      : status === 'enriching' ? (chance(0.5, rng) ? 1 : 0)
      : status === 'enriched' ? intBetween(1, 2, rng)
      : intBetween(2, 3, rng); // outreaching+

    const hasEmail = enrichmentDepth >= 1;
    const hasLinkedin = enrichmentDepth >= 1 && chance(0.8, rng);
    const hasPhone = enrichmentDepth >= 2 && chance(0.6, rng);
    const hasWhatsapp = enrichmentDepth >= 3 && chance(0.5, rng);

    const icpScore = status === 'discarded'
      ? intBetween(15, 45, rng)
      : status === 'converted'
        ? intBetween(80, 98, rng)
        : intBetween(45, 95, rng);

    const lvl1PersonId = intBetween(10000, 99999, rng);

    const handle = `${firstName}.${lastName}`.toLowerCase();
    const companyDomain = company.toLowerCase().replace(/\s+/g, '') + '.com';

    leads.push({
      lead_id: String(intBetween(70000, 99999, rng)),
      campaign_id: campaign.campaign_id,
      campaign_name: campaign.campaign_name,

      person_name: personName,
      person_email: hasEmail ? `${handle}@${companyDomain}` : null,
      person_role: pick(ROLES, rng),
      person_seniority: pick(SENIORITY, rng),

      company_id: String(intBetween(1000, 9999, rng)),
      company_name: company,
      company_industry: industry,
      company_country: country,
      company_size: pick(EMPLOYEE_RANGES, rng),
      company_revenue: intBetween(1, 50, rng) * 1_000_000,
      revenue: pick(REVENUE_RANGES, rng),
      nr_employees: pick(EMPLOYEE_RANGES, rng),
      size_range: pick(EMPLOYEE_RANGES, rng),

      status,
      lead_substatus: substatusFor(status, rng),
      icp_score: icpScore,
      icp_fit: fitForScore(icpScore),

      // Stored with the lvl1_id as filename (matches the real Appwrite pattern).
      profile_picture_url: null,
      profile_picture_storage_id: `person_${lvl1PersonId}`,

      city,
      state: null,
      country,

      created_at: toIsoDaysAgo(intBetween(1, 60, rng)),
      last_enriched: enrichmentDepth > 0 ? toIsoDaysAgo(intBetween(0, 14, rng)) : null,

      // Populated channel-id fields — these drive the channels[] array in the
      // UniBox mapper and the *_enriched flags in the lead-detail generator.
      person_phone: hasPhone ? `+${intBetween(1, 48, rng)} 6${intBetween(10, 99, rng)} ${intBetween(100, 999, rng)} ${intBetween(100, 999, rng)}` : null,
      person_mobile: hasPhone ? `+${intBetween(1, 48, rng)} 6${intBetween(10, 99, rng)} ${intBetween(100, 999, rng)} ${intBetween(100, 999, rng)}` : null,
      person_linkedin: hasLinkedin ? `https://linkedin.com/in/${handle}` : null,
      person_website: hasEmail ? `https://${companyDomain}` : null,
      linkedin_id: hasLinkedin ? handle : null,
      phone_id: hasPhone ? String(intBetween(100000, 999999, rng)) : null,
      whatsapp_id: hasWhatsapp ? String(intBetween(100000, 999999, rng)) : null,

      company_email: hasEmail ? `hello@${companyDomain}` : null,
      company_website: `https://${companyDomain}`,
      company_linkedin: `https://linkedin.com/company/${company.toLowerCase().replace(/\s+/g, '')}`,

      lvl1_person: { id: lvl1PersonId, name: personName, country, country_code: countryCode, city, timezone },
      lvl1_company: { id: intBetween(1000, 9999, rng), name: company, industry, country },
    } satisfies LeadCardResponse);
  }

  return { leads, metrics, total: count };
};

/**
 * Generate leads for every campaign AND stamp the recomputed metrics back onto
 * each campaign. Returns the leads grouped by campaign id for easy lookup.
 */
export const generateMockLeadsForAllCampaigns = (
  campaigns: CampaignMetrics[],
  volume: Exclude<MockVolume, 'none'>,
): Record<string, LeadCardResponse[]> => {
  const byCampaign: Record<string, LeadCardResponse[]> = {};
  campaigns.forEach((campaign) => {
    const { leads, metrics, total } = generateMockLeadsForCampaign(campaign, volume);
    byCampaign[campaign.campaign_id] = leads;
    campaign.leads_by_status = metrics;
    campaign.total_leads = total;
  });
  return byCampaign;
};
